import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetItinerary, type ItineraryBandResponse } from '../api';
import { exportItineraryPdf, buildItineraryDayDoc } from '../lib/itinerary';
import { apiGetDaysheetLayoutEffective } from '../api';
import PdfPreview from '../components/PdfPreview';
import type { DaysheetLayout } from '../lib/daysheetLayout';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

function fmtDate(d?: string): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

function typeIcon(et?: string): string {
  const e = (et || '').toLowerCase();
  if (e === 'show' || e === 'concert') return '🎫';
  if (e === 'festival') return '🎪';
  if (e === 'travel') return '🚐';
  if (e === 'off') return '🚫';
  return '📌';
}

/**
 * Tour Itinerary page — the band-wide, multi-day itinerary view (2026-09-04).
 * Consumes the existing /api/itinerary/:bandId aggregate (which was previously
 * only used by the offline preloader) and renders every day chronologically,
 * with an "Export Itinerary PDF" button (one daysheet per day + overview).
 * This is the central tour-manager deliverable that was missing.
 */
export default function ItineraryPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { profiles, activeProfileId } = useProfileContext();
  const activeBand = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;

  const [data, setData] = useState<ItineraryBandResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // ── Aperçu d'un jour, sous sa ligne (2026-09-20) ──
  // UNE seule ligne ouverte à la fois : la clé de la ligne dépliée.
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Le docDef est figé à l'ouverture : PdfPreview re-rend le PDF à CHAQUE
  // changement d'identité du docDef, donc le reconstruire à chaque render
  // relancerait une rasterisation serveur par frappe/re-render.
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [daysheetLayout, setDaysheetLayout] = useState<DaysheetLayout | null>(null);
  // Masquer les dates passées par défaut (même convention que la page Shows).
  const [hidePast, setHidePast] = useState(true);

  const load = useCallback(() => {
    if (!activeProfileId) { setData(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    apiGetItinerary(activeProfileId)
      .then(setData)
      .catch((e: any) => setError(e?.message || 'Failed to load itinerary'))
      .finally(() => setLoading(false));
  }, [activeProfileId]);

  useEffect(() => { load(); setOpenKey(null); setPreviewDoc(null); }, [load]);

  // Layout daysheet effectif : chargé UNE fois, réutilisé par l'export ET l'aperçu
  // (le jour imprimé et le jour aperçu doivent être rigoureusement identiques).
  const ensureLayout = useCallback(async (): Promise<DaysheetLayout | null> => {
    if (daysheetLayout) return daysheetLayout;
    if (!activeProfileId) return null;
    const l = await apiGetDaysheetLayoutEffective(activeProfileId).catch(() => null);
    const layout = (l as DaysheetLayout) || null;
    setDaysheetLayout(layout);
    return layout;
  }, [daysheetLayout, activeProfileId]);

  const togglePreview = useCallback(async (key: string, day: any) => {
    if (openKey === key) { setOpenKey(null); setPreviewDoc(null); return; }
    setOpenKey(key);
    setPreviewDoc(null);
    setPreviewBusy(true);
    try {
      const layout = await ensureLayout();
      setPreviewDoc(buildItineraryDayDoc(day, {
        band: data?.band ? { id: data.band.id, name: data.band.name } : null,
        daysheetLayout: layout,
      }));
    } finally {
      setPreviewBusy(false);
    }
  }, [openKey, ensureLayout, data]);

  const exportPdf = async () => {
    if (!activeProfileId || !data) return;
    setExporting(true);
    try {
      const daysheetLayoutEff = await ensureLayout();
      const url = await exportItineraryPdf({
        band: data.band ? { id: data.band.id, name: data.band.name } : null,
        days: (data.days || []).map((d) => ({
          show: d.show,
          roadmap: d.roadmap,
          flights: d.flights,
          rooming: d.rooming,
          accommodations: d.accommodations,
          guests: d.guests,
          team: d.team,
        })),
        daysheetLayout: daysheetLayoutEff as any,
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(activeBand?.name || 'band').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-itinerary.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      alert(t('itinerary.exportFailed', { msg: e?.message || e }));
    } finally {
      setExporting(false);
    }
  };

  const days = data?.days || [];

  // Filtre d'affichage uniquement : le PDF exporté contient TOUJOURS tous les jours.
  const visibleDays = useMemo(() => {
    if (!hidePast) return days;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return days.filter((d) => {
      const date = (d.show || {}).date;
      // Un jour sans date reste affiché (on ne masque que ce qui est daté).
      if (!date) return true;
      return new Date(date).getTime() >= today.getTime();
    });
  }, [days, hidePast]);

  return (
    <div ref={denseRef} className="page">
      <PageHeader
        title={withPageIcon('itinerary.pageTitle', t('itinerary.pageTitle'))}
        actions={
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
              <input
                type="checkbox"
                checked={hidePast}
                onChange={(e) => setHidePast(e.target.checked)}
              />
              {t('shows.hidePast')}
            </label>
            <button
              className="btn btn-primary"
              onClick={() => void exportPdf()}
              disabled={exporting || days.length === 0}
            >
              {exporting ? t('itinerary.exporting') : t('itinerary.export')}
            </button>
          </>
        }
      />

      {activeBand && <p className="text-dim" style={{ marginTop: 0 }}>{t('itinerary.pageFor', { band: activeBand.name })}</p>}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('itinerary.pageLoading')}</p>}

      {!loading && visibleDays.length === 0 && (
        <p className="text-dim">{t('itinerary.pageEmpty')}</p>
      )}

      {!loading && visibleDays.length > 0 && (
        <div className="table-wrapper" style={{ maxWidth: 1000 }}>
          <table className="tbl-dense">
            <thead>
              <tr>
                <th>{t('itinerary.dayCol')}</th>
                <th>{t('itinerary.showCol')}</th>
                <th>{t('itinerary.placeCol')}</th>
                <th>{t('itinerary.typeCol')}</th>
                <th>{t('itinerary.teamCol')}</th>
                <th>{t('itinerary.previewCol')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleDays.map((d, i) => {
                const s = d.show || {};
                const city = [s.venue_name, s.city, s.country].filter(Boolean).join(' · ');
                // Clé STABLE (indépendante de la position) : le filtre des dates
                // passées ne doit pas déplacer l'aperçu resté ouvert.
                const rowKey = `${s.id || s.date || i}-${s.date || ''}`;
                const isOpen = openKey === rowKey;
                return (
                  <Fragment key={rowKey}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(s.date)}</td>
                      <td style={{ fontWeight: 600 }}>{typeIcon(s.event_type)} {s.name || '—'}</td>
                      <td>{city || '—'}</td>
                      <td>{s.event_type || '—'}</td>
                      <td>{(d.team || []).length}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-small btn-secondary"
                          aria-expanded={isOpen}
                          aria-controls={`itinerary-preview-${rowKey}`}
                          onClick={() => void togglePreview(rowKey, d)}
                        >
                          {isOpen ? `✕ ${t('itinerary.previewClose')}` : `👁 ${t('itinerary.preview')}`}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="itinerary-preview-row">
                        <td colSpan={6} className="itinerary-preview-cell" id={`itinerary-preview-${rowKey}`}>
                          <p className="itinerary-preview-caption">
                            {fmtDate(s.date)} · {typeIcon(s.event_type)} {s.name || '—'}{city ? ` · ${city}` : ''}
                          </p>
                          <div className="itinerary-preview-body">
                            {previewBusy || !previewDoc
                              ? <div className="rider-preview-placeholder">{t('itinerary.pageLoading')}</div>
                              : <PdfPreview docDef={previewDoc} height={540} />}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
